import {LitElement, html} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {navigator} from 'lit-element-router';
import {msg} from '@lit/localize';
import {connect} from 'pwa-helpers';
import {getValueForLanguage} from '../../util/getValueForLanguage';
import store from '../../store';
import style from '../../../../scss/header.scss';
import {logout} from '../../slices/accountSlice';
import {navigateTo} from '../../slices/statusSlice';
import {
	selectAccountInitials,
	selectCurrentProfile,
	selectCurrentProfileId,
	selectIsOwnerOfCurrentProfile,
	selectLanguage,
	selectProfileDisplayName,
	selectProfileSeriesLabel,
	selectRole,
	selectTenant,
} from '../../selectors';
import {trimObject} from '../../util/trimObject';
import {getTenantData} from '../../../tenants';

@customElement('arc-header')
export class Header extends connect(store)(navigator(LitElement)) {
	static styles = [style];

	static properties = {
		profileId: {type: String},
		profileDisplayName: {type: String},
	};

	constructor() {
		super();
		this.isMenuOpen = false;
		this.profileId = null;
		this.profileDisplayName = '';
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.tenant = selectTenant(state);
		this.loggedIn = state.account.data.loggedIn;
		this.account = state.account.data.account;
		this.lang = selectLanguage(state);
		this.profileId = selectCurrentProfileId(state);
		this.profileDisplayName = selectProfileDisplayName(state);
		this.seriesLabel = selectProfileSeriesLabel(state);

		this.role = selectRole(state);
	}

	async onLogout() {
		await store.dispatch(logout());
		store.dispatch(navigateTo({route: 'home', params: {language: this.lang}}));
	}

	toggleMenu() {
		this.isMenuOpen = !this.isMenuOpen;
		this.requestUpdate();
	}

	render() {
		const s = store.getState();
		return this.loggedIn
			? html`
					<div class="header">
							${when(
								this.profileId,
								() => html`
									${when(
										selectProfileDisplayName(s),
										() => html` <div class="profileName">${selectProfileDisplayName(s)}</div>`
									)}
									<arc-header-dropdown-menu
										class="desktop"
										label="${getTenantData(this.tenant, 'productname')}"
										dropdownId="mainmenu"
									>
										<div class="headerDropdownContentInfo">
											<span>${msg('Profile')}: ${selectProfileDisplayName(s)}</span>
										</div>
										<div class="headerDropdownContentLink">
											<arc-routerlink
												route="artworks"
												.params=${{id: this.profileId}}
												.query=${trimObject(this.artworkQuery)}
												noUnderline
												whiteStyle
											>
												${msg('Edit Artworks')}
											</arc-routerlink>
										</div>
										<div class="headerDropdownContentLink">
											<arc-routerlink
												route="artworks-selected"
												.params=${{id: this.profileId}}
												noUnderline
												whiteStyle
											>
												${msg('Selected artworks')}
											</arc-routerlink>
										</div>
										<div class="headerDropdownContentLink">
											<arc-routerlink
												route="artworks-bin"
												.params=${{id: this.profileId}}
												noUnderline
												whiteStyle
											>
												${msg('Removed artworks')}
											</arc-routerlink>
										</div>
										<div class="headerDropdownContentLink">
											<arc-routerlink
												route="series-list"
												.params=${{id: this.profileId}}
												noUnderline
												whiteStyle
											>
												${when(
													this.lang === 'de',
													() => html`${this.seriesLabel} bearbeiten`,
													() => html`Edit ${this.seriesLabel}`
												)}
											</arc-routerlink>
										</div>
										<hr />
										<div class="headerDropdownContentLink">
											<arc-routerlink
												route="artist"
												.params=${{id: this.profileId}}
												noUnderline
												whiteStyle
											>
												${msg('Edit Profile')}
											</arc-routerlink>
										</div>
										<div class="headerDropdownContentLink">
											<arc-routerlink
												route="artist-cv"
												.params=${{id: this.profileId}}
												noUnderline
												whiteStyle
											>
												${msg('Edit CVs')}
											</arc-routerlink>
										</div>
										<div class="headerDropdownContentLink">
											<arc-routerlink
												route="artist-settings"
												.params=${{id: this.profileId}}
												noUnderline
												whiteStyle
											>
												${msg('Profile Settings')}
											</arc-routerlink>
										</div>
									</arc-header-dropdown-menu>
								`
							)}
							<div class="space"></div>
							${when(
								this.loggedIn && (this.role === 'admin' || this.role === 'operator'),
								() => html` <arc-header-dropdown-menu
									class="headerAdminMenu desktop"
									label="Admin"
									dropdownId="admin"
								>
									<arc-is-permitted
										.requirements=${[
											'account/viewUser',
											'account/viewAdmin',
											'account/viewOperator',
										]}
									>
										<div class="headerDropdownContentLink">
											<arc-routerlink route="admin-accounts" noUnderline whiteStyle>
												${msg('Accounts')}
											</arc-routerlink>
										</div>
										<div class="headerDropdownContentLink">
											<arc-routerlink route="admin-activities" noUnderline whiteStyle>
												${msg('Activity Log')}
											</arc-routerlink>
										</div>
									</arc-is-permitted>
								</arc-header-dropdown-menu>`
							)}
							${when(
								this.loggedIn,
								() => html` <arc-header-dropdown-menu
									class="headerAccountMenu desktop"
									label="${selectAccountInitials(s)}"
									labelStyle="icon"
									menuAlignment="right"
									dropdownId="account"
								>
									<div class="headerDropdownContentInfo">
										${msg('Account')}: ${this.account.name} (${this.account.email})
									</div>
									<div class="headerDropdownContentLink">
										<arc-routerlink
											route="account"
											.params=${{id: this.account?.id}}
											noUnderline
											whiteStyle
										>
											${msg('Account Settings')}
										</arc-routerlink>
									</div>
									<!--
									<div class="headerDropdownContentLink plan">
										<arc-routerlink route="choose-plan" noUnderline>
											<span>${msg('Subscription Settings')}</span>
										</arc-routerlink>
									</div>
									-->
									<div class="headerDropdownContentLink">
										<arc-routerlink
											route="account-profiles"
											.params=${{id: this.account?.id}}
											noUnderline
											whiteStyle
										>
											${msg('Profiles')}
										</arc-routerlink>
									</div>

									${when(
										selectIsOwnerOfCurrentProfile(s),
										() => html`
											<div class="headerDropdownContentLink">
												<arc-routerlink
													route="artist-share"
													.params=${{id: this.profileId}}
													noUnderline
													whiteStyle
												>
													${msg('Collaboration')}
												</arc-routerlink>
											</div>
										`
									)}

									<div class="headerDropdownContentLink">
										<arc-routerlink route="home" @click=${this.onLogout} noUnderline whiteStyle>
											${msg('Logout')}
										</arc-routerlink>
									</div>
								</arc-header-dropdown-menu>`,
								() => html`
									<div class="headerItem desktop login">
										<arc-routerlink route="home" noUnderline whiteStyle>
											${msg('Login')}
										</arc-routerlink>
									</div>
								`
							)}
							<!--
							<div class="headerItem headerMenu mobileOnly ${this.isMenuOpen ? 'toggled' : ''}">
								<div class="headerMenuTitle">${msg('Menu')}</div>
								<div class="headerMenuIcon" @click="${this.toggleMenu}">
									<div class="line ${classMap({lineOpen: this.isMenuOpen})}"></div>
									<div class="line ${classMap({lineOpen: this.isMenuOpen})}"></div>
									<div class="line ${classMap({lineOpen: this.isMenuOpen})}"></div>
								</div>
							</div>
							-->
						</div>
					</div>
					<!--
					<div class="mobileOverlay">
						<arc-menu-overlay
							.isOpen="${this.isMenuOpen}"
							.loggedIn="${this.loggedIn}"
							.profileDisplayName="${selectProfileDisplayName(s)}"
							@menu-closed="${() => (this.isMenuOpen = false)}"
						></arc-menu-overlay>
					</div>
					-->
			  `
			: null;
	}
}
